import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type CountryApiRow,
  type CurrencyApiRow,
  getCountries,
  getCurrencies,
  getStatesProvinces,
  type ListQuery,
  type ListResponse,
  type StateProvinceApiRow,
} from '@/api/reference-admin';
import type { DataTableResponse } from '@/components/data-table';

export type TabValue = 'countries' | 'states' | 'currencies';
export type ReferenceDataType = CountryApiRow | StateProvinceApiRow | CurrencyApiRow;

interface UseReferenceDataReturn {
  data: ListResponse<ReferenceDataType> | undefined;
  tableData: DataTableResponse<ReferenceDataType> | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useReferenceData = (
  activeTab: TabValue,
  filters: ListQuery
): UseReferenceDataReturn => {
  const [data, setData] = useState<ListResponse<ReferenceDataType> | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      // Only show loading spinner if we don't have data yet (initial load)
      if (!data) {
        setIsLoading(true);
      }
      setError(null);

      let result: ListResponse<ReferenceDataType>;

      switch (activeTab) {
        case 'countries':
          result = (await getCountries(filters)) as ListResponse<ReferenceDataType>;
          break;
        case 'states':
          result = (await getStatesProvinces(filters)) as ListResponse<ReferenceDataType>;
          break;
        case 'currencies':
          result = (await getCurrencies(filters)) as ListResponse<ReferenceDataType>;
          break;
        default:
          throw new Error(`Unknown tab: ${activeTab}`);
      }

      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load reference data');
      setData(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoized table data transformation
  const tableData = useMemo(() => {
    if (!data) return undefined;

    return {
      data: data.data,
      pagination: {
        page: data.pagination.page,
        pageSize: data.pagination.pageSize,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      },
    };
  }, [data]);

  return {
    data,
    tableData,
    isLoading,
    error,
    refetch: fetchData,
  };
};
